/**
 * RecentlyVisited widget — surfaces the operator's navigation history
 * from localStorage (no backend).
 *
 * Wave 3.6 migration: previous mock hardcoded 6 pages with fake
 * timestamps. Now reads from the shared `useRecentlyVisited` store
 * (hooks/useRecentlyVisited.ts), which is populated on every
 * route change by MainLayout's useTrackRecentlyVisited hook.
 *
 * Path-to-icon mapping lives here so the widget picks a reasonable
 * glyph for each category without ballooning the shared store's
 * schema. Unmatched paths fall back to a folder icon.
 *
 * Click routes via react-router's useNavigate (same as QuickActions)
 * so the page load reuses the SPA shell rather than a hard reload.
 */

import React, { useEffect, useState } from 'react';
import {
  Box, List, ListItem, ListItemIcon, ListItemText, Paper, Typography,
  useTheme,
} from '@mui/material';
import {
  Apartment, Assignment, BeachAccess, Build, Business, Event,
  EventNote, FolderOpen, Group, Insights, IntegrationInstructions,
  Lock, NotificationsNone, Person, Schedule, Settings, ViewList,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getRecentlyVisited, type VisitedPage,
} from '../../../../hooks/useRecentlyVisited';
import { useTranslation } from 'react-i18next';
import { formatRelativeTime } from './widgetDisplay';
import WidgetEmptyState from '../../../../components/primitives/WidgetEmptyState';
import { HistoryOutlined } from '@mui/icons-material';

// Path → icon. First matching regex wins. Order: most-specific
// first so e.g. /attendance/leave/balances picks BeachAccess before
// the /attendance generic fallback resolves Schedule.
const ICON_MAP: Array<{ test: RegExp; icon: React.ReactNode }> = [
  // Account. Missing until 2026-08-01 for the same reason the title rule was: it is
  // the one MainLayout route with no side menu, so nothing prompted an entry.
  { test: /^\/account\/security/, icon: <Lock /> },

  // Organization
  { test: /^\/organization\/workers\/active/, icon: <Group /> },
  { test: /^\/organization\/workers\/former/, icon: <Group /> },
  { test: /^\/organization\/workers\/documents/, icon: <FolderOpen /> },
  { test: /^\/organization\/workers\/face-templates/, icon: <Person /> },
  { test: /^\/organization\/workers/, icon: <Group /> },
  { test: /^\/organization\/worker-groups/, icon: <Group /> },
  { test: /^\/organization\/departments/, icon: <Apartment /> },
  { test: /^\/organization\/positions/, icon: <Person /> },
  { test: /^\/organization\/employment-types/, icon: <Person /> },
  { test: /^\/organization\/chart/, icon: <Apartment /> },
  { test: /^\/organization\/events/, icon: <Event /> },
  { test: /^\/organization\/announcements/, icon: <NotificationsNone /> },
  { test: /^\/organization\/branches-locations/, icon: <Business /> },
  { test: /^\/organization\/profile/, icon: <Business /> },
  { test: /^\/organization/, icon: <Business /> },

  // Attendance
  { test: /^\/attendance\/rules/, icon: <Settings /> },
  { test: /^\/attendance\/shifts/, icon: <Schedule /> },
  { test: /^\/attendance\/schedules/, icon: <EventNote /> },
  { test: /^\/attendance\/leave/, icon: <BeachAccess /> },
  { test: /^\/attendance\/geofences/, icon: <Business /> },
  { test: /^\/attendance\/time-entries/, icon: <Schedule /> },
  { test: /^\/attendance\/approvals/, icon: <Assignment /> },
  { test: /^\/attendance\/calculation/, icon: <Insights /> },
  { test: /^\/attendance\/reports/, icon: <Insights /> },
  { test: /^\/attendance/, icon: <Schedule /> },

  // Processes
  { test: /^\/processes\/designer/, icon: <Build /> },
  { test: /^\/processes\/tasks/, icon: <Assignment /> },
  { test: /^\/processes\/requests/, icon: <Assignment /> },
  { test: /^\/processes\/instances/, icon: <ViewList /> },
  { test: /^\/processes\/policies/, icon: <Settings /> },
  { test: /^\/processes\/form-components/, icon: <Build /> },
  { test: /^\/processes\/nodes/, icon: <Build /> },
  { test: /^\/processes\/analytics/, icon: <Insights /> },
  { test: /^\/processes/, icon: <Build /> },

  // Integrations
  { test: /^\/integrations/, icon: <IntegrationInstructions /> },

  // System
  { test: /^\/system\/access-control/, icon: <Lock /> },
  { test: /^\/system\/audit-logs/, icon: <ViewList /> },
  { test: /^\/system\/settings\/notifications/, icon: <NotificationsNone /> },
  { test: /^\/system\/settings/, icon: <Settings /> },
  { test: /^\/system\/billing/, icon: <Settings /> },
  { test: /^\/system\/support/, icon: <Settings /> },
  { test: /^\/system/, icon: <Settings /> },
];

function iconFor(path: string): React.ReactNode {
  const match = ICON_MAP.find((m) => m.test.test(path));
  return match ? match.icon : <FolderOpen />;
}

/**
 * The page's localized name, or the generic label when it has none.
 *
 * `item.title` is an i18n KEY, never display text. It can be null (no rule matched)
 * and, for entries written before 2026-08-01, it can be a humanized English string
 * left by the old fallback in `titleFor` — which never resolved, and is why
 * /account/security read "Page". Both cases land on the generic label rather than
 * putting English on screen, so old localStorage degrades quietly instead of
 * leaking.
 *
 * `src/i18n/recentlyVisitedTitles.test.ts` asserts no CURRENT MainLayout route can
 * reach the generic branch, so this is a fallback for stale storage, not for gaps.
 */
function labelFor(
  item: VisitedPage,
  t: (key: string) => string,
  i18n: { exists: (key: string) => boolean },
): string {
  const key = item.title
    ? `dashboard:recentlyVisited.titles.${item.title}`
    : null;
  return key && i18n.exists(key)
    ? t(key)
    : t('dashboard:widgets.recentlyVisited.genericPage');
}

const RecentlyVisited: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [items, setItems] = useState<VisitedPage[]>([]);

  useEffect(() => {
    setItems(getRecentlyVisited());
  }, []);

  if (items.length === 0) {
    return (
      <WidgetEmptyState icon={HistoryOutlined} title={t('dashboard:widgets.recentlyVisited.noVisits')} />
    );
  }

  return (
    <Box sx={{ height: '100%' }}>
      <List sx={{ py: 0 }}>
        {items.slice(0, 6).map((item) => (
          <ListItem
            key={item.path}
            component={Paper}
            elevation={0}
            onClick={() => navigate(item.path)}
            sx={{
              py: 0.75,
              mb: 0,
              borderRadius: 1,
              bgcolor: 'background.paper',
              transition: 'background-color 80ms ease',
              '&:hover': { bgcolor: 'grey.50', cursor: 'pointer' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.main }}>
              {iconFor(item.path)}
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2"
                  sx={{ fontWeight: 600, color: 'primary.main',
                    '&:hover': { textDecoration: 'underline' } }}>
                  {labelFor(item, t, i18n)}
                </Typography>
              }
              secondary={
                <Typography variant="caption"
                  sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  {formatRelativeTime(item.visited_at, t, i18n.language)}
                </Typography>
              }
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default RecentlyVisited;
